import React from 'react';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import DashboardReducer from '../../../reducers/dashboard.js';
import HeroUIBreadcrumb from '../../breadcrumbs/HeroUIBreadcrumb.js';
import { getOrganizationUnique } from '../../../api/utils.js';
import WhatsNewButton from '../../common/WhatsNewDrawer/WhatsNewButton.js';

/**
 * ProfileHeader — breadcrumbs + icon badge + title + subtitle, the header the
 * other V2 pages use (Resources, Users & Roles, My Dashboard), with the page's
 * actions on the right and What's New beside the title, as on those pages.
 * The changelog has no profile area; `settings` (merged with `general` by the
 * backend) is the one Security Settings uses too.
 *
 * It replaces the twig's legacy trail (those `breadcrumb` blocks are empty).
 *
 * HeroUIBreadcrumb reads the org menu from Redux (`state.dashboard`). The
 * profile pages have no store of their own, so the breadcrumb gets a small one
 * here — the single reducer the Hangars entry wires — instead of every profile
 * entrypoint growing a Provider. Built once at module load with RTK's
 * `configureStore` (thunk included), NOT `configs/create-store.js`: that one
 * also mounts the page-indication root, which must never happen during render.
 */
const breadcrumbStore = configureStore({
	reducer: { dashboard: DashboardReducer },
	preloadedState: { dashboard: { userMenu: [], isLoading: false, error: null } }
});

function ProfileHeader({ icon: Icon, title, subtitle, actions = null }) {
	// An administrator opens a member's profile from Users & Roles (the legacy
	// trail's "Organization Users"); keep that step in the trail.
	const orgUnique = getOrganizationUnique() || '';
	const customItems = orgUnique
		? [{ label: 'Users & Roles', href: `/org/${orgUnique}/roles/users/` }]
		: [];

	return (
		<div className="flex flex-col">
			<Provider store={breadcrumbStore}>
				<HeroUIBreadcrumb currentPage={title} customItems={customItems} />
			</Provider>

			<header className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-3 min-w-0">
					<div className="p-2.5 rounded-xl bg-primary-500/10 ring-1 ring-primary-500/20 shrink-0">
						<Icon className="w-6 h-6 text-primary-600" aria-hidden="true" />
					</div>
					<div className="flex flex-col gap-1 min-w-0">
						<div className="flex items-center gap-2">
							<h1 className="m-0">
								<span className="text-[24px] font-bold text-foreground">{title}</span>
							</h1>
							<WhatsNewButton area="settings" />
						</div>
						{subtitle && <p className="text-[13px] text-default-500 m-0">{subtitle}</p>}
					</div>
				</div>
				{actions}
			</header>
		</div>
	);
}

export default ProfileHeader;
